import type {Editor} from 'grapesjs';
import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {isUserLoggedIn, isUserPremium} from '@Libraries/user.library';
import {Icon} from '@Components/icon-v2';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {
  EMAIL_MAKER_AI_REWRITE_DROPDOWN_SINGUP_SOURCE,
  EMAIL_MAKER_AI_REWRITE_GENERATION_OPTIONS,
  EMAIL_MAKER_AI_REWRITE_PREVIOUS_PROMPT,
  EMAIL_MAKER_AI_REWRITE_PROMPT,
  TOOLBAR_AI_REWRITE_DROPDOWN_ANCHOR,
} from '@/email-maker/grapesjs-email-editor-plugin/src/email-maker.types';
import {Dropdown} from '@Components/dropdown-v2';
import {type AiRedesignPromptBoxProps, type SelectedPromptOptionValues} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {AiRedesignPromptBox} from '@Components/ai-resdesign-prompt-box';
import {EmailCampaignAiRewriteSource, useGetRemainingCreditsForEmailCampaignAiContent} from '@Libraries/ai-email-campaign-content-library';
import styles from './email-maker-ai-rewrite-prompt-box-dropdown.module.scss';
import {isMobileWidth} from '@Libraries/email-campaigns-library';
import {openAiRedesignPromptBoxModal} from '@Modals/ai-redesign-prompt-box-modal';
import {
  executeRewriteSelectedComponent,
  getAiRewritePromptBoxProps,
  getSelectedComponentsPromptBoxIssue,
  isSelectedComponenetsPromptBoxLoading,
  removeSelectedComponentsRewriteIssue,
} from '@Libraries/email-campaign-ai-rewrite-library';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from "@Components/circular-progress-loader";
import {openAuthenticateModal} from '@Modals/authenticate-modal/authenticate-modal-library';

export interface EmailMakerAiRewritePromptBoxDropdownProps {
  editor: Editor;
  showPromptBoxOnRender?: boolean;
}

export function EmailMakerAiRewritePromptBoxDropdown({editor, ...props}: EmailMakerAiRewritePromptBoxDropdownProps): ReactElement | undefined {
  const selectedComponent = editor.getSelected();
  const usageSource = EmailCampaignAiRewriteSource.EMAIL_MAKER_AI_REWRITE_DROPDOWN;
  const remainingCredits = useGetRemainingCreditsForEmailCampaignAiContent();
  const rewriteIssue = selectedComponent ? getSelectedComponentsPromptBoxIssue(selectedComponent, usageSource) : undefined;
  const isLoading = selectedComponent ? isSelectedComponenetsPromptBoxLoading(selectedComponent) : false;
  const isMobile = isMobileWidth();
  const [showDropdown, setShowDropdown] = useState(props.showPromptBoxOnRender);
  const DROPDOWN_LEFT_OFFSET = 12;

  useEffect(() => {
    if (!rewriteIssue) {
      return;
    }

    if (isMobile) {
      openPromptBoxModal();
      return;
    }

    setShowDropdown(true);
  }, [rewriteIssue]);

  useEffect(() => {
    if (!showDropdown) {
      return;
    }

    document.addEventListener('touchstart', closePopupListener);
    document.addEventListener('mousedown', closePopupListener);

    return (): void => {
      document.removeEventListener('touchstart', closePopupListener);
      document.removeEventListener('mousedown', closePopupListener);
    };
  }, [showDropdown]);

  useEffect(() => {
    const handleBeforeUnload = (): void => {
      sessionStorage.removeItem(EMAIL_MAKER_AI_REWRITE_GENERATION_OPTIONS);
      sessionStorage.removeItem(EMAIL_MAKER_AI_REWRITE_PREVIOUS_PROMPT);
      sessionStorage.removeItem(EMAIL_MAKER_AI_REWRITE_PROMPT);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return (): void => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  if (!selectedComponent) {
    return;
  }

  const closePopupListener = (e: MouseEvent | TouchEvent): void => {
    const target = e.target as HTMLElement;

    if (!document.getElementById(TOOLBAR_AI_REWRITE_DROPDOWN_ANCHOR)?.contains(target)) {
      setShowDropdown(false);
      if (rewriteIssue) {
        removeSelectedComponentsRewriteIssue(selectedComponent);
      }
    }
  };

  const rewriteSelectedComponent = async (prompt: string, selectedPromptOptionValues: SelectedPromptOptionValues): Promise<void> => {
    setShowDropdown(false);

    await executeRewriteSelectedComponent(editor, selectedComponent, usageSource, rewriteIssue, prompt, selectedPromptOptionValues);
  };

  const getAiPromptBoxProps = (): AiRedesignPromptBoxProps => {
    const promptBoxClass = isMobile ? styles.mobilePromptBox : styles.promptBox;
    return getAiRewritePromptBoxProps(rewriteSelectedComponent, rewriteIssue, remainingCredits, promptBoxClass, editor);
  };

  const getAiTextPromptBoxPopup = (): ReactElement => {
    return <AiRedesignPromptBox {...getAiPromptBoxProps()} />;
  };

  const getSelectorForDropdown = (): ReactElement => {
    const icon = <Icon icon="icon-ai-caption" className={`content-ai`} size={IconSize.SIZE_ICON_16} />;
    return <div className={!isMobile ? styles.dropdownSelector : ''}>{icon}</div>;
  };

  const handleOnClick = (): void => {
    if (rewriteIssue) {
      removeSelectedComponentsRewriteIssue(selectedComponent);
    }
    setShowDropdown(!showDropdown);
  };

  const getToolbarElement = (): HTMLElement | null => {
    return document.getElementById(TOOLBAR_AI_REWRITE_DROPDOWN_ANCHOR);
  };

  const setToolbarElementTooltipContent = (tooltipContent: string): void => {
    const toolbarElement = getToolbarElement();
    toolbarElement?.setAttribute('title', tooltipContent);
  };

  const hideTooltip = (): void => {
    setToolbarElementTooltipContent('');
  };

  const openPromptBoxModal = (): void => {
    openAiRedesignPromptBoxModal({
      title: window.i18next.t('pmwjs_ai_rewrite'),
      icon: 'icon-ai-caption',
      onClose: () => {
        if (rewriteIssue) removeSelectedComponentsRewriteIssue(selectedComponent);
      },
      ...getAiPromptBoxProps(),
    });
  };

  const getPromptBoxDropdown = (): ReactElement => {
    if (isMobile) {
      if (showDropdown) {
        openPromptBoxModal();
      }
      return <div onClick={openPromptBoxModal}>{getSelectorForDropdown()}</div>;
    }

    return (
      <Dropdown
        open={showDropdown}
        popup={getAiTextPromptBoxPopup()}
        selector={getSelectorForDropdown()}
        popupClassName={styles.popupContainer}
        popUpHasCustomWidth
        popUpHasCustomHeight
        alignment={ALIGNMENT_TYPE.BOTTOM_END}
        shouldFlip
        doesCloseOnOuterClick
        doCloseOnInnerClick={false}
        onClick={handleOnClick}
        onOpen={hideTooltip}
        leftOffset={DROPDOWN_LEFT_OFFSET}
      />
    );
  };

  const getLoaderIcon = (): ReactElement => {
    return (
      <div className={`flex-center ${styles.loader}`}>
        <CircularProgressLoader color={LOADER_COLOR.AI_DARK} size={LOADER_SIZE.XSMALL} />
      </div>
    );
  };

  const getUpsellDialogComponent = (): ReactElement => {
    return (
      <div
        onClick={() => {
          if (!isUserLoggedIn()) {
            openAuthenticateModal({
              fbPermissions: '',
              signupSource: EMAIL_MAKER_AI_REWRITE_DROPDOWN_SINGUP_SOURCE,
              panelText: window.i18next.t('pmwjs_make_account_to_save_email'),
              panelSubtext: window.i18next.t('pmwjs_login_emails_promotion_subtext'),
              onLoginSuccess: async (): Promise<void> => {
                await editor.runCommand(window.PMW.emails.commands.SAVE_TO_DB_AND_CACHE);
                window.location.reload();
              },
            });
            return;
          }

          if (!isUserPremium()) {
            void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_AI_REWRITE_EMAIL_CAMPAIGN_TEXT_COMPONENTS_CONTENT as string);
            return;
          }
        }}
        className={styles.premiumIconContainer}
      >
        <Icon icon="icon-crown" type={IconType.PREMIUM} className={`${styles.premiumIcon} color-premium`} size={IconSize.SIZE_ICON_12} />
        {getSelectorForDropdown()}
      </div>
    );
  };

  const getToolbarComponent = (): ReactElement => {
    if (isUserLoggedIn() && isUserPremium()) {
      return isLoading ? getLoaderIcon() : getPromptBoxDropdown();
    }

    return getUpsellDialogComponent();
  };

  return getToolbarComponent();
}
